import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

import * as facescan from "../utils/careplix-scan-sdk/facescan";
import { useAuth } from "../utils/auth";
import {
  api_key,
  authEmployeeID,
  sdkURL,
  separateBase64Image,
} from "../utils/constants";
import { logAnalyticsEvent } from "../utils/analytics";

import { ReactComponent as SwitchCamera } from "../assets/icons/SwitchCamera.svg";
import Loader from "../assets/images/loader.gif";
import Failure from "../assets/images/failureNew.webp";
import BackArrow from "../assets/images/back-arrow.svg";
import {
  derma_api_key,
  derma_api_secret_key,
  face_match_api_key,
  face_match_api_secret_key,
  sdkUnityURL,
} from "../utils/api-url-list";

const AllCombinedFaceScan = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userData } = useAuth();
  const playerRef = useRef(null);
  const videoRef = useRef();
  const canvasRef = useRef();
  const eyeBlinkConfidenceData = useRef([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "error",
    message: "---",
    progress: 0,
    timeElapsed: 0,
    isLiteMode: false,
    eyeBlinkConfidence: 0,
  });
  const causeList = useRef([]);

  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    const isPlatformIOS = /iPhone|iPad|iPod|Macintosh|Mac/i.test(
      navigator.userAgent
    );
    const playVideo = () => {
      if (playerRef.current?.paused) playerRef.current.play();
    };
    const pauseVideo = (reset = false) => {
      if (playerRef.current) {
        playerRef.current.pause();
        if (reset) playerRef.current.currentTime = 0;
      }
    };
    let duration = 0;
    const addCause = (cause = "") => {
      const possibleErrors = {
        FCINT01: "Please check your internet connection & try again.",
        FCSCN01: "Face not detected.",
        FCSCN02:
          "No suitable subject detected. If the issue persists, consider adjusting the framing or removing any obstructions from the view.",
        FCSCN03: "Please move your face inside of visible space.",
        FCSCN04: "Please hold your phone a bit farther.",
        FCSCN05: "Please hold your phone a bit closer.",
        FCSCN06: "Please don't tilt your face.",
        FCSCN07: "Please look straight at the camera.",
        FCSCN08: "Sorry, Face Scan is not supported on this device.",
        CMSCN01: "Sorry we're unable to compute the signal. Please try again.",
        CMUSR01: "We are not able to access the Camera. Please try again.",
        CMUSR02:
          "App functionality disabled in the Background. Keep it in the Foreground for proper operation.",
      };
      const errorCode =
        Object.keys(possibleErrors).find(
          (code) => possibleErrors[code] === cause
        ) ?? "UNKNOWN";
      const lastItem = causeList.current[causeList.current.length - 1];
      if (lastItem?.code !== errorCode) {
        if (!(lastItem?.duration > 3)) causeList.current.pop();
        causeList.current.push({ code: errorCode, duration });
      }
    };
    facescan.onFrame((fd) => {
      setScanFrameData(fd);
      if (fd.type === "error") {
        if (state?.selectedAssesments?.vision === true) {
          eyeBlinkConfidenceData.current = [];
          pauseVideo(true);
        }
        addCause(fd.message);
      } else {
        if (state?.selectedAssesments?.vision === true) {
          eyeBlinkConfidenceData.current.push({
            confidence: fd.eyeBlinkConfidence,
            timestamp: fd.timeElapsed,
            progress: fd.progress,
          });
          playVideo();
        }
      }
      duration = Math.round(fd.timeElapsed / 1000);
    });
    facescan.onError((err) => {
      console.error(err);
      if (state?.selectedAssesments?.vision === true) pauseVideo();
      setError(err.message);
      addCause(err.message);
      fetch(sdkURL("/vitals/update-scan-status"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key,
          scan_token: state?.scanToken,
          employee_id: userData.profile_id,
          fire_type: "on_error",
          fire_reason: "facescan.onError",
          error_list: causeList.current,
        }),
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (!json.statusCode?.toString().startsWith("2"))
            throw new Error(json.message ?? "Unable to send Error logs.");
        })
        .catch(console.error);
    });
    let faceImage = null;
    let faceMatchResultData = undefined;
    facescan.onFaceImageCapture(async (imageDataB64) => {
      faceImage = imageDataB64;
      if (
        state?.selectedAssesments?.faceMatch === true &&
        !faceMatchResultData
      ) {
        try {
          const resp = await fetch(
            sdkUnityURL("/facematch/facematch-image-upload"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: face_match_api_secret_key,
              },
              body: JSON.stringify({
                api_key: face_match_api_key,
                action: "client_initiated",
                run_facematch: true,
                facematch_token: state?.faceMatchToken,
                candidate_images: [separateBase64Image(imageDataB64)],
              }),
            }
          );
          const json = await resp.json();
          if (json?.statusCode?.toString().startsWith("2")) {
            faceMatchResultData = json?.facematch_result?.data?.find?.(
              (i) => i.is_matched === true
            );
            if (!faceMatchResultData)
              throw new Error("No matching face found.");
          } else throw new Error(json?.message ?? "Failed to Fetch Result");
        } catch (err) {
          facescan.stopScan();
          console.error(err);
          if (state?.selectedAssesments?.vision === true) pauseVideo();
          setError(err.message);
          addCause(err.message);
          fetch(sdkURL("/vitals/update-scan-status"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              scan_token: state?.scanToken,
              employee_id: userData.profile_id,
              fire_type: "on_error",
              fire_reason: "facescan.onError",
              error_list: causeList.current,
            }),
          })
            .then((resp) => resp.json())
            .then((json) => {
              if (!json.statusCode?.toString().startsWith("2"))
                throw new Error(json.message ?? "Unable to send Error logs.");
            })
            .catch(console.error);
        }
      }
    });
    facescan.onScanFinish(
      async ({
        raw_intensity,
        ppg_time,
        average_fps,
        leftEyeImageData,
        rightEyeImageData,
      }) => {
        let scanToken = "";
        try {
          setAnalyzing(true);
          pauseVideo();
          const addScanResp = await fetch(sdkURL("/vitals/add-scan"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              package: "DEFAULT",
              api_key,
              scan_token: state.scanToken,
              posture: "resting",
              dob: userData.dob,
              gender: userData.gender,
              metadata: {
                physiological_scores: {
                  height: userData.height,
                  weight: userData.weight,
                },
                ppg_time: ppg_time,
                raw_intensity: raw_intensity,
                device: `RPPG_CAREPLIX_FACE_${
                  isPlatformIOS ? "IOS" : "ANDROID"
                }`,
                fps: average_fps,
              },
            }),
            signal: controller.signal,
          });
          const addScanRespJSON = await addScanResp.json();
          if (addScanRespJSON?.statusCode?.toString().startsWith("2")) {
            let dermaResult = undefined;
            if (state?.selectedAssesments?.derma === true) {
              try {
                const { image, type } = separateBase64Image(faceImage);
                const response = await fetch(
                  sdkUnityURL("/derma/skin-assessment"),
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: derma_api_secret_key,
                    },
                    body: JSON.stringify({
                      api_key: derma_api_key,
                      employee_id: authEmployeeID,
                      type: "face",
                      enc_image: image,
                      file_ext: type,
                    }),
                  }
                );
                const resultJSON = await response.json();
                if (resultJSON?.statusCode?.toString().startsWith("2"))
                  dermaResult = {
                    selectedArea: "face",
                    image: faceImage,
                    analysis: resultJSON.result,
                  };
                else
                  throw new Error(
                    resultJSON?.message ?? "Failed to Fetch Result"
                  );
              } catch (err) {
                console.error(err);
                dermaResult = undefined;
              }
            }
            navigate(
              state?.selectedAssesments?.vision === true
                ? "/vision-survey"
                : state?.selectedAssesments?.pulmo === true
                ? "/voice-audio-sample"
                : state?.selectedAssesments?.dgmer === true
                ? "/digimer-assessment"
                : "/all-combined-result",
              {
                state: {
                  selectedAssesments: state?.selectedAssesments,
                  dgmerAnswers: state?.dgmerAnswers,
                  scanResult:
                    state?.selectedAssesments?.scan === true
                      ? addScanRespJSON
                      : undefined,
                  faceMatchResult:
                    state?.selectedAssesments?.faceMatch === true &&
                    !!faceMatchResultData
                      ? { faceImage, ...faceMatchResultData }
                      : undefined,
                  visionResult:
                    state?.selectedAssesments?.vision === true
                      ? {
                          eyeBlinkData: eyeBlinkConfidenceData.current,
                          leftEyeImageData,
                          rightEyeImageData,
                        }
                      : undefined,
                  dermaResult,
                },
                replace: true,
              }
            );
            const scan_id = addScanRespJSON?.scan_id;
            logAnalyticsEvent("face_scan_completed", { scan_id });
            logAnalyticsEvent("scan_result", { scan_id });
          } else
            throw new Error(
              addScanRespJSON?.message ?? "Failed to Fetch Result"
            );
        } catch (err) {
          console.error(err);
          setError(err.message);
        } finally {
          fetch(sdkURL("/vitals/update-scan-status"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              scan_token: scanToken,
              employee_id: userData.profile_id,
              fire_type: "on_success",
              fire_reason: "facescan.onScanFinish",
              error_list: causeList.current,
            }),
          })
            .then((resp) => resp.json())
            .then((json) => {
              if (!json.statusCode?.toString().startsWith("2"))
                throw new Error(json.message ?? "Unable to send Error logs.");
            })
            .catch(console.error);
        }
      }
    );
    const initScan = () => {
      if (canvasRef.current !== null && videoRef.current !== null) {
        facescan
          .startScan({
            livelinessDetectionDuration: state?.livelinessDuration,
            strictness: state?.strictness,
            canvasElement: canvasRef.current,
            videoElement: videoRef.current,
          })
          .then(() => {
            console.log("Scan Started");
            duration = 0;
            causeList.current = [];
          })
          .catch(() => console.log("Scan Failed"))
          .finally(() => setLoading(false));
      }
    };
    if (state?.selectedAssesments?.vision === true)
      playerRef.current?.addEventListener?.(
        isPlatformIOS ? "loadedmetadata" : "canplay",
        initScan,
        { signal: controller.signal, once: true, passive: true }
      );
    else initScan();
    return () => {
      facescan.stopScan();
      controller.abort();
    };
  }, [navigate, userData, state]);

  const scanMessages = useMemo(
    () =>
      [
        "We burn calories while we are asleep because brain activity requires energy",
        "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
        "Always sleep on your back straight since it allows your neck and spine in a neutral position",
        "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
        "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
        "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
        "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
        "Lack of sleep can lead to heart disease, heart attack, or stroke",
        "Heart pumps about 2,000 gallons of blood every day",
        "Your heart will beat about 115,000 times each day",
      ].sort(() => Math.random() - 0.5),
    []
  );

  const currentMessage = useMemo(() => {
    if (scanFrameData.type === "error") return "---";
    const timeElapsed = scanFrameData.timeElapsed / 1000;
    if (timeElapsed <= 5)
      return "Keep your head upright and look straight at the camera";
    else if (timeElapsed <= 10)
      return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 15)
      return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 20) return scanMessages[0];
    else if (timeElapsed <= 25) return scanMessages[1];
    else if (timeElapsed <= 30) return scanMessages[2];
    else if (timeElapsed <= 35) return scanMessages[3];
    else if (timeElapsed <= 40) return scanMessages[4];
    else if (timeElapsed <= 45) return scanMessages[5];
    else if (timeElapsed <= 50) return scanMessages[6];
    else if (timeElapsed <= 55) return scanMessages[7];
    else if (timeElapsed <= 60) return scanMessages[8];
    else if (timeElapsed <= 65) return scanMessages[9];
  }, [scanFrameData.timeElapsed, scanFrameData.type, scanMessages]);

  return (
    <main className="h-dvh w-dvw overflow-hidden flex flex-col bg-gradient-to-b from-primary to-transparent to-50% tab-screen">
      <div className="flex items-center justify-center absolute left-6 lg:left-14 right-6 top-6 tab-screen">
        <Link
          to={-1}
          className=" w-9 h-9 bg-[#2D2D2D] rounded-full absolute left-0 flex flex-row justify-center items-center"
        >
          <img src={BackArrow} alt="" />
        </Link>
        <h2 className="text-xl font-medium text-white lg:text-2xl">
          Face Scan
        </h2>
      </div>
      <div className="circle-blur w-[80%] h-60 mx-auto absolute top-20 left-0 right-0 blur-[80px]" />
      <section className="mt-[5rem] mx-5 rounded-[20px] bg-[#A3A2A8]">
        <div className="relative h-[calc(100dvh-17rem)] rounded-xl overflow-hidden">
          <canvas
            ref={canvasRef}
            id="canvasOutput"
            className={
              state?.selectedAssesments?.vision === true
                ? "absolute z-50 h-36 bottom-8 right-8 rounded-2xl -scale-x-100"
                : "absolute inset-0 w-full h-full object-cover -scale-x-100"
            }
            // style={{
            //   visibility:
            //     state?.selectedAssesments?.vision === true
            //       ? "hidden"
            //       : "visible",
            // }}
          />
          {state?.selectedAssesments?.vision === true && (
            <video
              ref={playerRef}
              id="video-player"
              className="absolute left-1/2 -translate-x-1/2 h-full bg-black"
              src={`${process.env.PUBLIC_URL}/during-scan-video/${
                state?.videoIndex ?? 1
              }.mp4`}
              preload="auto"
              playsInline
              disablePictureInPicture
              disableRemotePlayback
            />
          )}
          <video
            ref={videoRef}
            id="videoInput"
            className="absolute top-6 right-6 h-px w-px bg-black"
            autoPlay
            muted
            playsInline
          />

          {scanFrameData.type === "scan" && (
            <div className="absolute left-0 right-0 animate-scan text-secondary/75" />
          )}

          {scanFrameData.type === "error" &&
            (scanFrameData.message.includes("Detection of low FPS.") ? (
              <div className="fixed z-50 top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center space-y-6 bg-primary text-white">
                <SwitchCamera className="h-16 w-16" />
                <p className="text-xl font-light whitespace-pre-line">
                  {scanFrameData.message.replace(/\. /g, ".\n")}
                </p>
              </div>
            ) : (
              <p className="absolute top-1/2 -translate-y-1/2 left-8 right-8 px-2 py-1 rounded-full bg-error/70 text-white text-center text-xs font-medium responsive-not-connect">
                {scanFrameData.message}
              </p>
            ))}
        </div>
      </section>

      <div className="shrink-0 min-h-48 flex flex-col bg-primary">
        <div className="relative shrink-0 h-[4px] bg-[#D9D9D9] rounded-full mt-6 mb-1 mx-5">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full bg-secondary shadow-inner transition-all duration-1000"
            style={{ width: `${scanFrameData.progress}%` }}
          />
        </div>
        <div className="relative grow m-4 p-4 pb-12 rounded-2xl border border-primary/30 bg-[#364560]">
          <h4 className="text-white font-semibold">
            {scanFrameData.type === "error"
              ? "Scan Stopped"
              : scanFrameData.message}
          </h4>
          <h6 className="mt-2.5 text-white text-xs leading-tight tracking-tight">
            {currentMessage}
          </h6>
          <button
            type="button"
            onClick={() => {
              facescan.stopScan();
              fetch(
                "https://sdk-staging.carenow.healthcare/vitals/update-scan-status",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    api_key: state?.partner_key,
                    employee_id: state?.partner_client_id,
                    scan_token: state?.scan_token,
                    device_model: state?.device_model,
                    fire_type: "on_halt",
                    fire_reason: "cancel-scan",
                    error_list: [],
                  }),
                }
              )
                .then((resp) => resp.json())
                .then((json) => {
                  if (!json.statusCode?.toString().startsWith("2"))
                    throw new Error(
                      json.message ?? "Unable to send Error logs."
                    );
                })
                .catch(console.error);
              navigate(-1);
            }}
            className="absolute bottom-4 right-4 text-secondary text-xxs font-medium"
          >
            &lt;&nbsp;Cancel Scan
          </button>
        </div>
      </div>

      {analyzing && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-32" src={Loader} alt="Analyzing..." />
          <h4 className="mt-8 text-xl font-bold">Analyzing Data</h4>
          <p className="mt-4 font-light">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
        </div>
      )}

      {error.length > 0 && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-44" src={Failure} alt="Failure..." />
          <p className="mt-8 text-xl font-bold">
            Sorry, we failed to calculate
            <br />
            your measurements
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line font-light">
            {error}
          </p>
          <button
            className="w-full xl:w-4/12 mt-8 px-5 py-2.5 rounded-full bg-white text-primary font-medium"
            type="button"
            onClick={() => {
              facescan.stopScan();
              navigate(-1);
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {loading && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center">
          <img className="shrink-0 h-20" src={Loader} alt="Loading..." />
          <h4 className="mt-20 text-xl font-bold">Downloading Scan modules</h4>
          <p className="mt-4 font-light">This might take few seconds....</p>
        </div>
      )}
    </main>
  );
};

export default AllCombinedFaceScan;
